import { defineMessages, type MessageDescriptor } from 'react-intl';
import type { RecutChip } from 'fym-shared/src/music24/musicVideoDoc';

/** The copy of the timeline and the recut box (design music-video Timeline, c5, c6). */
export const timelineMessages = defineMessages({
  title: { id: 'app.sml.musicVideo.timeline.title', defaultMessage: 'Timeline' },
  zoomLabel: { id: 'app.sml.musicVideo.timeline.zoomLabel', defaultMessage: 'Zoom' },
  zoomSong: { id: 'app.sml.musicVideo.timeline.zoomSong', defaultMessage: 'Whole song' },
  zoomSection: { id: 'app.sml.musicVideo.timeline.zoomSection', defaultMessage: 'Section' },
  zoomIn: { id: 'app.sml.musicVideo.timeline.zoomIn', defaultMessage: 'Zoom in' },
  zoomOut: { id: 'app.sml.musicVideo.timeline.zoomOut', defaultMessage: 'Zoom out' },
  zoomHint: {
    id: 'app.sml.musicVideo.timeline.zoomHint',
    defaultMessage: 'Zoom in to move the cuts',
  },
  scroll: { id: 'app.sml.musicVideo.timeline.scroll', defaultMessage: 'Scroll the timeline' },
  snap: { id: 'app.sml.musicVideo.timeline.snap', defaultMessage: 'Snap to the beat' },
  tempo: { id: 'app.sml.musicVideo.timeline.tempo', defaultMessage: '{bpm} BPM · from the song' },
  yourFootage: { id: 'app.sml.musicVideo.timeline.yourFootage', defaultMessage: 'Your footage' },
  recutAll: { id: 'app.sml.musicVideo.timeline.recutAll', defaultMessage: 'Recut the whole video' },
  laneTime: { id: 'app.sml.musicVideo.timeline.lane.time', defaultMessage: 'Time' },
  laneSections: { id: 'app.sml.musicVideo.timeline.lane.sections', defaultMessage: 'Sections' },
  laneFootage: { id: 'app.sml.musicVideo.timeline.lane.footage', defaultMessage: 'Footage' },
  laneText: { id: 'app.sml.musicVideo.timeline.lane.text', defaultMessage: 'Text' },
  laneLyrics: { id: 'app.sml.musicVideo.timeline.lane.lyrics', defaultMessage: 'Lyrics' },
  laneSong: { id: 'app.sml.musicVideo.timeline.lane.song', defaultMessage: 'Song' },
  songLocked: { id: 'app.sml.musicVideo.timeline.songLocked', defaultMessage: 'The song is locked' },
  snapTip: {
    id: 'app.sml.musicVideo.timeline.snapTip',
    defaultMessage: 'Snaps to bar {bar}, beat {beat} · {time}',
  },
  freeTip: { id: 'app.sml.musicVideo.timeline.freeTip', defaultMessage: 'Free · {time}' },
  clipLabel: { id: 'app.sml.musicVideo.timeline.clipLabel', defaultMessage: 'Clip {number}: {label}' },
  clipTitle: {
    id: 'app.sml.musicVideo.timeline.clipTitle',
    defaultMessage: '{label} · Drag to move it',
  },
  edgeLabel: { id: 'app.sml.musicVideo.timeline.edgeLabel', defaultMessage: 'Move the cut after clip {number}' },
  textTitle: { id: 'app.sml.musicVideo.timeline.textTitle', defaultMessage: 'Title · {title}' },
  textName: { id: 'app.sml.musicVideo.timeline.textName', defaultMessage: '{artist} — {title}' },
  textEndCard: { id: 'app.sml.musicVideo.timeline.textEndCard', defaultMessage: 'End card · SmartLink' },
  playHead: { id: 'app.sml.musicVideo.timeline.playHead', defaultMessage: 'Play head' },

  // The recut box (c6)
  recutAllHeading: { id: 'app.sml.musicVideo.recut.allHeading', defaultMessage: 'The whole video' },
  recutFacts: {
    id: 'app.sml.musicVideo.recut.facts',
    defaultMessage: '{from}–{to} · {count, plural, one {# clip} other {# clips}}',
  },
  recutEyebrow: { id: 'app.sml.musicVideo.recut.eyebrow', defaultMessage: 'Recut it' },
  recutPlaceholder: {
    id: 'app.sml.musicVideo.recut.placeholder',
    defaultMessage: 'Say what to change (optional), for example: more fire and city lights.',
  },
  recutButton: { id: 'app.sml.musicVideo.recut.button', defaultMessage: 'Recut {section} · no credits' },
  recutAllButton: { id: 'app.sml.musicVideo.recut.allButton', defaultMessage: 'Recut the whole video · no credits' },
  everyChorus: { id: 'app.sml.musicVideo.recut.everyChorus', defaultMessage: 'Use this cut for every chorus' },
  everyChorusDone: { id: 'app.sml.musicVideo.recut.everyChorusDone', defaultMessage: 'Every chorus has this cut now.' },
  cutLength: { id: 'app.sml.musicVideo.recut.cutLength', defaultMessage: 'Cut length' },
  cutLengthBeats: {
    id: 'app.sml.musicVideo.recut.cutLengthBeats',
    defaultMessage: '{beats, plural, one {# beat} other {# beats}}',
  },
  close: { id: 'app.sml.musicVideo.recut.close', defaultMessage: 'Close' },
  recutStarted: {
    id: 'app.sml.musicVideo.recut.started',
    defaultMessage: 'The AI recuts it now. The clips you keep stay.',
  },
  recutLimit: {
    id: 'app.sml.musicVideo.recut.limit',
    defaultMessage: 'You recut this song 30 times in the last hour. Try again later.',
  },
  recutBusy: { id: 'app.sml.musicVideo.recut.busy', defaultMessage: 'The AI still works on this cut.' },
  recutFailed: { id: 'app.sml.musicVideo.recut.failed', defaultMessage: 'The recut did not start. Try again.' },
});

/** The chips of the recut box (fym-shared `RECUT_CHIPS`). */
export const recutChipMessages: Readonly<Record<RecutChip, MessageDescriptor>> = defineMessages({
  fasterCuts: { id: 'app.sml.musicVideo.recut.chip.fasterCuts', defaultMessage: 'Faster cuts' },
  calmer: { id: 'app.sml.musicVideo.recut.chip.calmer', defaultMessage: 'Calmer' },
  moreOfMyFootage: { id: 'app.sml.musicVideo.recut.chip.moreOfMyFootage', defaultMessage: 'More of my footage' },
  noPeople: { id: 'app.sml.musicVideo.recut.chip.noPeople', defaultMessage: 'No people' },
  brighter: { id: 'app.sml.musicVideo.recut.chip.brighter', defaultMessage: 'Brighter' },
});
